import './HomepageTop5.less';

import React, { createContext, FC, memo, useContext, useEffect, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import SportsFilter from '../../common/SportsFilter';
import {
    top5BetById$,
    top5BetIdByMatchId$,
    top5BySportId$,
    top5CmsTournamentsLoaded$,
    top5MatchById$,
    top5MatchByTournamentId$,
    top5MatchesByTournamentId$,
    top5SportMatches$,
    top5SportMatchesLoaded$,
    top5sports$,
    top5SportsWithMatches$,
    top5TournamentNameById$,
    top5Tournaments$,
    top5tournamentsIds$,
    top5TournamentsLoaded$,
    top5TournamentSportId$,
    top5TournamentsWithMatches$,
    topRankingLoaded$,
    tournamentShowAllBets$$
} from '../subs';
import { loadMatches, loadTournaments, setShowAllBetsTournament, topEventsLoaded } from '../handlers';
import { ErrorBoundary } from '../../../common/ErrorBoundary';
import { SvgIcon } from '../../../icons/SvgIcon';
import { BET_TIPS } from '../../../common/constants';
import MatchIndex from '../../market-statistics/view/MatchIndex';
import { formatMatchDate } from '../../market-statistics/utils';
import { BetSelection } from '../../turboLive/components/BetSelection';
import { Map } from 'immutable';
import { FavoriteIcon, RadarIcon, StatsIcon, VideoIcon } from '../../turboLive/components/MatchIcons';
import clsx from 'clsx';
import i18n from '../../../common/i18n';
import { isFavoriteMatch$$ } from '../../turboLive/favorite/subs';
import { toggleFavoriteMatch } from '../../turboLive/favorite/handlers';
import BetLocationContext, { betLocation, BetPosition } from '../../../common/BetLocationContext';
import Dummy from '../../Dummy/Dummy';
import { isRadarStream, isVideoStream, setInfocentrumFromTab } from '../../turboLive/push/overview/handlers';
import { useIsBetradarWidgetAvailable } from '../../match-statistics/hooks/useIsBetradarWidgetAvailable';
import { FilterSwiper } from '../../filterSwiper/FilterSwiper';
import { MatchDetailLink } from './MatchDetailLink';
import { getTopTournamentsMatchesLimit } from '../utils';
import { isMobileChannel } from '../../../common/util';
import NuiBox from '../../nui-box/NuiBox';
import NuiTitle from '../../nui-title/NuiTitle';
import { MatchOpponents } from '../../turboLive/components/Match';
import { SportIconNui } from '../../../icons/SportIconNui';
import SportHeader from '../../sportHeader/SportHeader';
import HomepageTab from './HomepageTab';
import AnalysisButton from '../../analysis-btn/AnalysisButton';
import { getSportNameById } from '../../../common/utils/utils';

const DEFAULT_SPORT_ID = '1';
const DEFAULT_SPORT_NAME = 'futbal';

const Top5Context = createContext({
    betLocationSection: null
});

export const getMatchIds = topEvents => topEvents?.map(rd => rd.get('matchId')).toOrderedSet();

const HomepageTop5: FC<{ otherClass?: string; betLocationSection?: BetPosition; hideTitle?: boolean }> = ({
    otherClass,
    betLocationSection,
    hideTitle
}) => {
    const derefSub = useReframe();
    const top5sports = derefSub(top5sports$);
    const top5tournaments = derefSub(top5Tournaments$);
    const topRankingLoaded = derefSub(topRankingLoaded$);
    const isMobileView = isMobileChannel();
    const hasRankingData = top5sports?.size || top5tournaments?.size;
    return (
        <ErrorBoundary description="Homepage Top5">
            <Top5Context.Provider value={{ betLocationSection }}>
                {!topRankingLoaded || hasRankingData ? (
                    isMobileView ? (
                        <>
                            {!hideTitle && <NuiTitle title="TOP ZÁPASY" />}
                            {hasRankingData ? <HomepageTop5DataFetch top5sports={top5sports} /> : <HomepageTop5Dummy />}
                        </>
                    ) : (
                        <NuiBox noPad otherClass={clsx('overflow-hidden', otherClass)}>
                            {hasRankingData ? <HomepageTop5DataFetch top5sports={top5sports} /> : <HomepageTop5Dummy />}
                        </NuiBox>
                    )
                ) : null}
            </Top5Context.Provider>
        </ErrorBoundary>
    );
};

const HomepageTop5Dummy: FC = () => (
    <>
        <SportsFilter otherClass="bg-white">
            <HomepageTab sportId="1" label="TOP futbal" active />
            <HomepageTab sportId="3" label="TOP hokej" />
            <HomepageTab sportId="7" label="TOP tenis" />
        </SportsFilter>
        <HomepageTop5MatchesDummy />
    </>
);

const HomepageTop5MatchesDummy = () => (
    <>
        <HomepageTop5Header sportName={DEFAULT_SPORT_NAME} sportId={DEFAULT_SPORT_ID} />
        {isMobileChannel() ? (
            <div className={`flex flex-col flex-gap-10 px-20 pb-20 bg-${DEFAULT_SPORT_ID}`}>
                {Array.from({ length: 5 }).map((e, i) => (
                    <Dummy otherClass="bg-white br-radius-4 homepage-top5-match-dummy" key={i} />
                ))}
            </div>
        ) : (
            Array.from({ length: 5 }).map((e, i) => (
                <Dummy otherClass="br-b-default-light homepage-top5-match" key={i} />
            ))
        )}
    </>
);

const HomepageTop5DataFetch: FC<{ top5sports: any }> = ({ top5sports }) => {
    const derefSub = useReframe();
    const activeSportId = top5sports?.first()?.get('sportId');
    const topEvents = top5sports
        ?.map(sport => sport.get('topEvents'))
        .toList()
        .flatten(1);
    const matchesLoaded = derefSub(top5SportMatchesLoaded$(activeSportId));
    const cmsTournamentsLoaded = derefSub(top5CmsTournamentsLoaded$);
    const tournamentsLoaded = derefSub(top5TournamentsLoaded$);
    const tournamentsIds = derefSub(top5tournamentsIds$);

    useEffect(() => {
        if (topEvents && !matchesLoaded) {
            const matchIds = getMatchIds(topEvents);
            loadMatches(matchIds, topEventsLoaded);
        }
    }, [topEvents, matchesLoaded]);

    useEffect(() => {
        if (tournamentsIds?.size && cmsTournamentsLoaded && !tournamentsLoaded) {
            loadTournaments(tournamentsIds);
        }
    }, [cmsTournamentsLoaded, tournamentsLoaded, tournamentsIds]);

    return cmsTournamentsLoaded || tournamentsLoaded ? <HomepageTop5Wrapper /> : <HomepageTop5Dummy />;
};

const HomepageTop5Wrapper: FC = () => {
    const derefSub = useReframe();
    const top5SportsMatches = derefSub(top5SportsWithMatches$);
    const top5TournamentsMatches = derefSub(top5TournamentsWithMatches$);

    return top5SportsMatches || top5TournamentsMatches ? (
        <HomepageTop5View
            top5SportsMatches={top5SportsMatches?.keySeq().toList()}
            top5TournamentsMatches={top5TournamentsMatches?.keySeq().toList()}
        />
    ) : null;
};

const HomepageTop5View: FC<{ top5SportsMatches; top5TournamentsMatches: any }> = ({
    top5SportsMatches,
    top5TournamentsMatches
}) => {
    const derefSub = useReframe();
    const top5sports = derefSub(top5sports$);
    const top5tournaments = derefSub(top5Tournaments$);
    const filteredSports = top5sports?.filter(t => top5SportsMatches?.contains(t.get('sportId')?.toString()));
    const filteredTournaments = top5tournaments?.filter(t =>
        top5TournamentsMatches?.contains(t.get('tournamentId')?.toString())
    );

    const [activeSportId, setActiveSportId] = useState(parseInt(filteredSports?.first()?.get('sportId')));
    const [activeTournamentId, setActiveTournamentId] = useState(
        !activeSportId ? parseInt(filteredTournaments?.first()?.get('tournamentId')) : null
    );
    const tournamentIndex =
        activeTournamentId && filteredTournaments?.keySeq().findIndex(k => k === `${activeTournamentId}`);

    const sportButtons = filteredSports?.valueSeq().map(sport => {
        const sportId = sport.get('sportId');
        const sportName = sport.get('sportName');
        const top5sportEvents = derefSub(top5BySportId$(sportId));
        const top5sportMatches = derefSub(top5SportMatches$(sportId));
        return (
            <HomepageTab
                sportId={sportId}
                label={`TOP ${sportName}`}
                onClick={() => {
                    setActiveSportId(sportId);
                    setActiveTournamentId(null);
                    dispatch(setInfocentrumFromTab(top5sportEvents, top5sportMatches));
                }}
                active={sportId === activeSportId}
                key={`s${sportId}`}
            />
        );
    });

    const tournamentButtons = filteredTournaments?.valueSeq().map(tournament => {
        const tournamentId = tournament.get('tournamentId');
        const sportId = tournament.get('sportId');
        const top5TournamentMatches = derefSub(top5MatchesByTournamentId$(tournamentId));
        return (
            <TournamentFilterItem
                tournamentId={tournamentId}
                sportId={sportId}
                onClick={() => {
                    setActiveTournamentId(tournamentId);
                    setActiveSportId(null);
                    dispatch(setInfocentrumFromTab(null, top5TournamentMatches));
                }}
                active={`${tournamentId}` === `${activeTournamentId}`}
                key={`t${tournamentId}`}
            />
        );
    });

    const buttons = sportButtons ? sportButtons.concat(tournamentButtons) : sportButtons || tournamentButtons;

    return activeSportId || activeTournamentId ? (
        <>
            <SportsFilter>
                <FilterSwiper refresh={top5tournaments} buttons={buttons} customCss="full-width" inline />
            </SportsFilter>
            <HomepageTop5Matches
                activeSportId={activeSportId}
                activeTournamentId={activeTournamentId}
                tournamentIndex={tournamentIndex}
            />
        </>
    ) : null;
};

const TournamentFilterItem: FC<{ tournamentId; sportId: string; onClick: any; active: boolean }> = ({
    tournamentId,
    sportId,
    onClick,
    active
}) => {
    const derefSub = useReframe();
    const tournamentName = derefSub(top5TournamentNameById$(tournamentId));
    return tournamentName ? (
        <HomepageTab sportId={sportId} label={tournamentName} onClick={onClick} active={active} />
    ) : null;
};

const getBetLocation = location => {
    switch (location) {
        case 'futbal':
            return betLocation.HP_TOP_FOOTBALL;
        case 'hokej':
            return betLocation.HP_TOP_HOCKEY;
        case 'tenis':
            return betLocation.HP_TOP_TENNIS;
        case 'TOURNAMENT_1':
            return betLocation.HP_TOP_TOURNAMENT_1;
        case 'TOURNAMENT_2':
            return betLocation.HP_TOP_TOURNAMENT_2;
        case 'TOURNAMENT_3':
            return betLocation.HP_TOP_TOURNAMENT_3;
        case 'TOURNAMENT_4':
            return betLocation.HP_TOP_TOURNAMENT_4;
        default:
            return betLocation.HP_TOP_TOURNAMENT_5;
    }
};

export const HomepageTop5Matches: FC<{
    activeSportId?: number;
    activeTournamentId: number;
    tournamentIndex: number;
}> = ({ activeSportId, activeTournamentId, tournamentIndex }) => {
    const derefSub = useReframe();
    const top5Ctx = useContext(Top5Context);
    const eventSportId = activeSportId || derefSub(top5TournamentSportId$(activeTournamentId));
    const tournamentName = activeTournamentId && derefSub(top5TournamentNameById$(activeTournamentId));
    const sportName = getSportNameById(eventSportId, false);
    const betLocation = activeSportId ? sportName : `TOURNAMENT_${++tournamentIndex}`;
    const betLocationSection = top5Ctx.betLocationSection || getBetLocation(betLocation);
    const isMobileView = isMobileChannel();
    const top5Matches = (
        <HomepageTop5MatchesBoxes
            activeSportId={activeSportId}
            activeTournamentId={activeTournamentId}
            sportName={sportName}
        />
    );
    return (
        <ErrorBoundary description="Homepage Top5 matches">
            <BetLocationContext.Provider value={{ betLocationSection }}>
                <HomepageTop5Header sportName={sportName} sportId={eventSportId} tournamentName={tournamentName} />
                {isMobileView ? (
                    <div className={`px-20 pb-20 bg-${eventSportId} flex flex-col flex-gap-10 p-rel`}>
                        {top5Matches}
                    </div>
                ) : (
                    top5Matches
                )}
            </BetLocationContext.Provider>
        </ErrorBoundary>
    );
};

export const HomepageTop5Header: FC<{ sportId: string; sportName: string; tournamentName?: string }> = ({
    sportId,
    sportName,
    tournamentName
}) => {
    const mobileChannel = isMobileChannel();
    return (
        <SportHeader
            sportId={sportId}
            sportName={sportName}
            otherClass={clsx(
                'py-15',
                mobileChannel ? 'p-rel' : 'f-condensed pl-15 fs-20 c-white text-extra-bold flex items-center'
            )}
        >
            {!mobileChannel && (
                <>
                    <SportIconNui sportId={`${sportId}`} sizeX="24" sizeY="24" className="c-white mr-10" />
                    {tournamentName || 'TOP 10'}
                </>
            )}
        </SportHeader>
    );
};

export const HomepageTop5MatchesBoxes: FC<{
    activeSportId?: number;
    activeTournamentId: number;
    sportName: string;
}> = ({ activeSportId, activeTournamentId, sportName }) => {
    const derefSub = useReframe();
    const topEvents = activeSportId
        ? derefSub(top5BySportId$(activeSportId))
        : derefSub(top5MatchesByTournamentId$(activeTournamentId));
    const isMobileView = isMobileChannel();
    const showedAll = activeTournamentId && derefSub(tournamentShowAllBets$$(activeTournamentId));
    const topMatchesLimit = getTopTournamentsMatchesLimit(isMobileView);
    const visibleShowMore = topEvents.size > topMatchesLimit;
    return topEvents?.size && sportName ? (
        <>
            {topEvents
                .valueSeq()
                .slice(0, showedAll ? topEvents?.size : topMatchesLimit)
                .map((topEvent, i) => (
                    <HomepageTop5Match
                        activeSportId={activeSportId}
                        topEvent={topEvent}
                        sportName={sportName}
                        index={i + 1}
                        key={topEvent.get('matchId') || topEvent.get('sportEventId')}
                    />
                ))}
            {visibleShowMore && (
                <ToggleAllBetsButton
                    showedAll={showedAll}
                    activeTournamentId={activeTournamentId}
                    isMobileView={isMobileView}
                />
            )}
        </>
    ) : (
        <HomepageTop5MatchesDummy />
    );
};

const ToggleAllBetsButton: FC<{
    showedAll: boolean;
    activeTournamentId: number;
    isMobileView: boolean;
}> = ({ showedAll, activeTournamentId, isMobileView }) => {
    return (
        <div className={clsx('flex justify-center', { 'mt-5': isMobileView })}>
            <div
                className="text-center d-block c-white py-10 pointer"
                onClick={() => dispatch(setShowAllBetsTournament(activeTournamentId, !showedAll))}
            >
                {showedAll ? i18n('homepage.top5.showLessMatches') : i18n('homepage.top5.showAllMatches')}
            </div>
        </div>
    );
};

const HomepageTop5Match: FC<{ topEvent: any; sportName; activeSportId: number; index: number }> = memo(
    ({ topEvent, sportName, activeSportId, index }) => {
        const derefSub = useReframe();
        const matchId = topEvent.get('matchId') || topEvent.get('sportEventId');
        const tournamentId = topEvent.get('tournamentId');
        const match = activeSportId
            ? derefSub(top5MatchById$(activeSportId, matchId))
            : derefSub(top5MatchByTournamentId$(tournamentId, matchId));
        const isFavoriteMatch = derefSub(isFavoriteMatch$$(`${matchId}`));
        if (!match) {
            return null;
        }
        const participants = match.get('participants');
        const betId = topEvent.get('betId');
        const betCount = match.get('betCount');
        const statisticsId = match.get('statisticsId');
        const expiration = match.get('expiration');
        const articleId = match.get('articleId');
        const toggleFavorite = () => dispatch(toggleFavoriteMatch(sportName, match, true));
        const isMobileView = isMobileChannel();
        const isFirst = index === 1;
        return (
            <>
                <div
                    className={clsx(
                        'flex',
                        isMobileView
                            ? 'flex-col bg-white br-radius-4'
                            : 'homepage-top5-match br-b-default-light items-center'
                    )}
                    data-atid="top5-box"
                >
                    {isMobileView ? (
                        <>
                            <div className="homepage-top5-match-info flex items-center br-b-default-light">
                                <HomepageTop5MatchTime index={index} sportName={sportName} expiration={expiration} />
                                <div className="flex-1 flex items-center justify-end">
                                    <HomepageTop5MatchMobileIcons
                                        match={match}
                                        articleId={articleId}
                                        statisticsId={statisticsId}
                                    />
                                    <FavoriteIcon toggle={toggleFavorite} active={isFavoriteMatch} iconClass="mx-10" />
                                </div>
                            </div>
                            <div className="homepage-top5-match-m flex items-center">
                                <HomepageTop5Opponents participants={participants} matchId={matchId} />
                                <HomepageTop5Bet
                                    matchId={matchId}
                                    sportId={activeSportId}
                                    betId={betId}
                                    isFirst={isFirst}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <HomepageTop5MatchTime index={index} sportName={sportName} expiration={expiration} />
                            <HomepageTop5Opponents participants={participants} matchId={matchId} />
                            <HomepageTop5MatchIcons match={match} otherClassName="br-l-default-light" />
                            <HomepageTop5Bet
                                matchId={matchId}
                                sportId={activeSportId}
                                betId={betId}
                                isFirst={isFirst}
                            />
                            <div className="homepage-top5-last flex self-stretch">
                                <FavoriteIcon toggle={toggleFavorite} active={isFavoriteMatch} iconClass="mx-10" />
                                <StatsIcon statisticsId={statisticsId} iconClass="match-icon-blue" usePlaceholder />
                                <HomepageTop5BetCount betCount={betCount} matchId={matchId} />
                            </div>
                        </>
                    )}
                </div>
            </>
        );
    }
);

export const IcoPlaceholder = () => <span className="px-10 mx-2 pt-8 pb-10" />;

const HomepageTop5MatchTime: FC<{ index: number; sportName; expiration: string }> = ({
    index,
    sportName,
    expiration
}) => {
    const isMobileView = isMobileChannel();
    return (
        <>
            <MatchIndex index={index} otherClass={`color-${sportName}`} />
            <div
                className={clsx(
                    'flex items-center self-stretch pl-10 c-black-50 br-l-default-light',
                    isMobileView ? 'fs-12' : 'br-r-default-light fs-10 homepage-top5-match-date'
                )}
            >
                {formatMatchDate(expiration)}
            </div>
        </>
    );
};

const HomepageTop5Opponents: FC<{ participants: any; matchId: string }> = ({ participants, matchId }) => {
    const home = participants?.get(0);
    const away = participants?.get(1);
    return (
        <MatchDetailLink matchId={matchId} className="flex-1">
            <MatchOpponents home={home} away={away} />
        </MatchDetailLink>
    );
};

const HomepageTop5BetCount: FC<{ betCount: number; matchId: string }> = ({ betCount, matchId }) =>
    betCount ? (
        <a
            href={`/tipovanie/zapas/${matchId}`}
            className={clsx(
                'flex items-center justify-end',
                isMobileChannel()
                    ? 'homepage-top5-bet-count c-black-30 fs-12'
                    : 'fs-13 text-bold pr-15 flex-1 c-black-25'
            )}
        >{`+${betCount}`}</a>
    ) : null;

export const HomepageTop5MatchIcons: FC<{ match: any; otherClassName?: string; disabled?: boolean }> = memo(
    ({ match, otherClassName, disabled = false }) => {
        const hasRadarStream = isRadarStream(match);
        const hasVideoStream = isVideoStream(match);
        const articleId = match.get('articleId');

        return (
            <div className={clsx('self-stretch flex px-3', otherClassName)}>
                <div className="flex flex-col justify-center">
                    {hasVideoStream ? <VideoIcon match={match} /> : <IcoPlaceholder />}
                    {hasRadarStream ? <RadarIcon match={match} disabled={disabled} /> : <IcoPlaceholder />}
                </div>
                <div className="flex flex-col justify-center">
                    {articleId ? <AnalysisButton postId={articleId} /> : <IcoPlaceholder />}
                    <IcoPlaceholder />
                </div>
            </div>
        );
    }
);

const HomepageTop5MatchMobileIcons: FC<{ match: any; statisticsId?; articleId?: string }> = ({
    match,
    articleId,
    statisticsId
}) => {
    const hasRadarStream = isRadarStream(match);
    const hasVideoStream = isVideoStream(match);
    const isBrAvaiable = useIsBetradarWidgetAvailable(match);
    const isBetradarWidgetAvailable = hasRadarStream && isBrAvaiable;
    return (
        <>
            {articleId && (
                <SvgIcon imgId="live-analyse" sizeX="16" sizeY="16" className="c-black-20 mx-8 flex-shrink-0" />
            )}
            {hasVideoStream ? <VideoIcon match={match} /> : <span className="px-8 mx-8" />}
            {isBetradarWidgetAvailable ? <RadarIcon match={match} /> : <span className="px-8 mx-8" />}
            {statisticsId ? (
                <SvgIcon imgId="live-stats" className="mx-10 c-black-20 my-10" sizeX="16" sizeY="16" />
            ) : (
                <span className="px-15 py-15 mr-3" />
            )}
        </>
    );
};

const HomepageTop5SelectionsLabels: FC<{ selectionsGrid: any; isMobileView?: boolean; label?: boolean }> = ({
    selectionsGrid,
    isMobileView,
    label
}) =>
    selectionsGrid ? (
        <div
            className={clsx(
                'p-abs flex c-white text-center',
                isMobileView ? 'homepage-top5-labels-m' : 'homepage-top5-labels p-rel'
            )}
        >
            {selectionsGrid.map(
                (row, i) =>
                    (i === 0 || !isMobileView) &&
                    row.map((cell, j) => (
                        <span className="homepage-top5-label flex-1 ellipsis fs-10 p-rel" key={`bt${i}${j}`}>
                            {BET_TIPS[cell.get('tip')]}
                        </span>
                    ))
            )}
            {!isMobileView && label && <span className="p-abs c-white fs-12 homepage-top5-market">{label}</span>}
        </div>
    ) : null;

const HomepageTop5Bet: FC<{ matchId; sportId?; betId; isFirst?: boolean }> = ({ matchId, sportId, betId, isFirst }) => {
    const derefSub = useReframe();
    const bet = sportId && betId ? derefSub(top5BetById$(betId)) : derefSub(top5BetIdByMatchId$(matchId));
    return <HomepageTop5BetView bet={bet} isFirst={isFirst} />;
};

const HomepageTop5BetView: FC<{ bet; isFirst?: boolean }> = ({ bet, isFirst }) => {
    if (!bet) {
        return null;
    }
    const betId = bet.get('betId');
    const getSelection = cell =>
        Map({
            name: cell.get('name'),
            code: cell.get('tip'),
            odds: cell.get('odds'),
            enabled: cell.get('enabled'),
            bold: cell.get('bold')
        });
    const selectionsGrid = bet.get('selectionGrid');
    const marketName = bet.get('header');
    const isMobileView = isMobileChannel();
    return selectionsGrid ? (
        <>
            {isFirst && isMobileView && marketName && (
                <span className="homepage-top5-market-m p-abs c-white fs-14">{marketName}</span>
            )}
            <div
                className={clsx('flex-1 flex self-stretch bg-black-3 br-l-default-light br-r-default-light', {
                    'p-rel': isFirst,
                    'homepage-top5-bet': !isMobileView
                })}
            >
                {isFirst && (
                    <HomepageTop5SelectionsLabels
                        selectionsGrid={selectionsGrid}
                        isMobileView={isMobileView}
                        label={marketName}
                    />
                )}
                {selectionsGrid.map(
                    (row, i) =>
                        (i === 0 || !isMobileView) &&
                        row.map((cell, j) => (
                            <BetSelection
                                selection={getSelection(cell)}
                                betId={`${betId}`}
                                channelEnabled
                                localized
                                stopped={false}
                                key={`${i}${j}${betId}`}
                            />
                        ))
                )}
            </div>
        </>
    ) : null;
};

export default HomepageTop5;
